// Custom Quick Buy Panel Functionality

// ---------------------------------------------------------------------------
// A11Y (accessibility report rows 7-8)
//
// This panel behaves like a modal dialog but was built without any of the
// keyboard/screen-reader plumbing: focus was never moved into it, Tab walked
// straight out of it into the page behind, Escape did nothing, and closing it
// left focus nowhere. The role/aria-modal/aria-labelledby attributes are in
// snippets/product-card.liquid; the behaviour is here.
//
// Nothing below changes how the panel looks or animates - the original
// hidden/is-opening/is-closing logic is untouched.
// ---------------------------------------------------------------------------

const QUICK_BUY_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'label[for]',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

function quickBuyFocusable(panel) {
  return Array.from(panel.querySelectorAll(QUICK_BUY_FOCUSABLE)).filter(
    (el) => el.offsetWidth > 0 || el.offsetHeight > 0
  );
}

// Escape closes; Tab / Shift+Tab cycle inside the panel instead of escaping it.
function quickBuyKeydown(event) {
  const panel = event.currentTarget;

  if (event.key === 'Escape') {
    event.stopPropagation();
    closePanel(panel, document.querySelector(`[aria-controls="${panel.id}"]`));
    return;
  }

  if (event.key !== 'Tab') return;

  const items = quickBuyFocusable(panel);
  if (items.length === 0) {
    event.preventDefault();
    return;
  }

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

// ---------------------------------------------------------------------------
// Outside-click dismissal.
//
// BUGFIX: this listener used to be registered inline when the panel opened, and
// removed ONLY from inside itself, i.e. only when an outside click actually fired
// it. Any other way of closing the panel - the X button, picking a size, and (once
// we added it) Escape - left the listener attached to `document` forever.
//
// The next click on the "+" trigger then hit two listeners in registration order:
// ours opened the panel, and the stale one closed it again in the same click. Note
// it could not be filtered out by its own guard, because `e.target` on that click
// is the <svg> inside the button, not the button, so `e.target !== trigger` was
// true. Net effect: after closing with Escape, the panel would not reopen.
//
// Now the listener is owned by openPanel/closePanel, so every close path removes
// it, and the guard uses trigger.contains() so child nodes count as the trigger.
// ---------------------------------------------------------------------------

function removeOutsideClickHandler(panel) {
  if (panel && panel._quickBuyOutsideClick) {
    document.removeEventListener('click', panel._quickBuyOutsideClick);
    panel._quickBuyOutsideClick = null;
  }
}

function addOutsideClickHandler(panel, trigger) {
  removeOutsideClickHandler(panel);

  const handler = function (e) {
    if (!panel.contains(e.target) && !trigger.contains(e.target)) {
      closePanel(panel, trigger);
    }
  };

  panel._quickBuyOutsideClick = handler;

  // Small delay so the click that opened the panel does not immediately close it.
  setTimeout(() => {
    // Skip if the panel was already closed again inside those few milliseconds.
    if (panel._quickBuyOutsideClick === handler) {
      document.addEventListener('click', handler);
    }
  }, 10);
}

// Helper functions for panel animations
function closePanel(panel, trigger) {
  if (panel && trigger) {
    trigger.setAttribute('aria-expanded', 'false');

    // Give focus back to the button that opened the panel - but only if focus is
    // currently inside it. Otherwise the panel was dismissed by an outside click
    // and yanking focus would fight the user.
    const focusWasInside = panel.contains(document.activeElement);
    panel.removeEventListener('keydown', quickBuyKeydown);
    removeOutsideClickHandler(panel);

    panel.classList.add('is-closing');
    setTimeout(() => {
      panel.hidden = true;
      panel.classList.remove('is-closing');
    }, 300);

    if (focusWasInside && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }
}

function openPanel(panel, trigger) {
  if (panel && trigger) {
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.classList.add('is-opening');
    setTimeout(() => {
      panel.classList.remove('is-opening');
    }, 300);

    panel.addEventListener('keydown', quickBuyKeydown);
    addOutsideClickHandler(panel, trigger);

    // Move focus to the dialog container itself (report row 7, point 1) rather
    // than to the first size, so the panel's name is announced before its options.
    panel.focus();
  }
}

// Use event delegation for all interactions to ensure compatibility with AJAX pagination
document.addEventListener('click', function(event) {
  // 1. Handle quick buy trigger clicks
  const quickBuyTrigger = event.target.closest('[data-custom-quick-buy-trigger]');
  if (quickBuyTrigger) {
    event.preventDefault();
    event.stopPropagation();
    
    const controlsId = quickBuyTrigger.getAttribute('aria-controls');
    const panel = document.getElementById(controlsId);
    
    if (panel) {
      const isExpanded = quickBuyTrigger.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        closePanel(panel, quickBuyTrigger);
      } else {
        // The outside-click listener is now owned by openPanel/closePanel so that
        // it is torn down on EVERY close path, not only when it fires itself.
        openPanel(panel, quickBuyTrigger);
      }
    }
    return; // Stop further processing
  }
  
  // 2. Handle close button clicks
  const closeButton = event.target.closest('[data-custom-quick-buy-close]');
  if (closeButton) {
    event.preventDefault();
    event.stopPropagation();
    
    const panel = closeButton.closest('.product-card__custom-quick-buy');
    if (panel) {
      const panelId = panel.getAttribute('id');
      const trigger = document.querySelector(`[aria-controls="${panelId}"]`);
      closePanel(panel, trigger);
    }
    return; // Stop further processing
  }
  
  // 3. Handle dimension item clicks for AJAX form submission
  const blockSwatch = event.target.closest('.block-swatch');
  if (blockSwatch) {
    const dimensionItem = blockSwatch.closest('.product-card__custom-quick-buy-dimension-item');
    if (dimensionItem) {
      const form = dimensionItem.querySelector('form[data-type="add-to-cart"]');
      if (form) {
        event.preventDefault();
        event.stopPropagation();
        
        // Add loading state
        blockSwatch.classList.add('is-loading');
        
        // Submit the form via AJAX to add the product to cart
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(response => response.json())
        .then(data => {
          // Remove loading state
          blockSwatch.classList.remove('is-loading');
          
          // Close the panel with animation
          const panel = dimensionItem.closest('.product-card__custom-quick-buy');
          if (panel) {
            const panelId = panel.getAttribute('id');
            const trigger = document.querySelector(`[aria-controls="${panelId}"]`);
            closePanel(panel, trigger);
          }
          
          // Update cart drawer and open it
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
          
          // Open cart drawer
          const cartDrawerToggle = document.querySelector('[aria-controls="cart-drawer"]');
          if (cartDrawerToggle) {
            cartDrawerToggle.click();
          }
        })
        .catch(error => {
          console.error('Error:', error);
          blockSwatch.classList.remove('is-loading');
        });
      }
    }
  }
});
